import { useState, useRef, useContext, useEffect } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import {
  checkQuestionThread,
  fetchBrains,
  getAAIToken,
  getDeepgramToken,
  saveChat,
} from "services/api";
import AAITranscriber from "services/aai-transcriber";
import DeepgramTranscriber from "services/deepgram-transcriber";
import WhiteHeaderSidebarLayout from "layouts/WhiteHeaderSidebarLayout";
import { ReactComponent as DoubleArrow } from "assets/icons/double-arrow.svg";
import Drawer from "components/Drawer";
import ChatBox from "components/ChatBoxV2";
import { AppContext } from "App";
import AllCases from "components/AllCases";
import styles from "./styles";

export default function AssistantFlowV2() {
  const authToken = localStorage.getItem("authToken");

  const sessionMessages = useSelector((state) => state.chat.selectedSessionMessages);

  const { notifyError } = useContext(AppContext);
  const navigate = useNavigate();

  const [processModel, setProcessModel] = useState("gpt");
  const [transcriptModel, setTranscriptModel] = useState("assembly-ai");

  const [assistant, setAssistant] = useState("default");
  const [allBrains, setAllBrains] = useState([
    { id: "default", name: "Default" }
  ]);

  const [isDrawerOpen, setIsDrawerOpen] = useState(true);
  const [chatHistory, setChatHistory] = useState([]);
  const [isAnswering, setIsAnswering] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [transcript, setTranscript] = useState("");
  const [summary, setSummary] = useState("");
  const [timer, setTimer] = useState(0);
  const [state, setState] = useState("empty"); // empty, idle, halting, recording, stopping, view-summary

  const threadIdRef = useRef(null);
  const transcriberRef = useRef(null);
  const boxRef = useRef(null);

  const handleDrawerToggle = () => {
    setIsDrawerOpen((current) => !current);
  };

  // Leaving this in for testing purposes.
  const doNothing = () => {
    console.warn("Skipping question");
  };

  const stopRecording = async (event) => {
    event.preventDefault();

    setState("stopping");
    await transcriberRef.current?.close();
    transcriberRef.current = null;

    const summary = await saveChat(
      transcript,
      threadIdRef.current,
      processModel
    ).catch((e) => notifyError(e.message, 4000));

    setSummary(summary);
    setState("idle");
  };

  const checkQuestion = async (message) => {
    const data = await checkQuestionThread(
      message,
      threadIdRef.current,
      processModel
    ).catch((e) => notifyError(e.message, 4000));

    if (data.questionDetected) {
      setIsAnswering(true);
      const { question } = data;
      setChatHistory((prev) => [...prev, { question, assistant }]);
    }
  };

  const getTranscriber = async () => {
    if (transcriptModel === "assembly-ai") {
      const token = await getAAIToken().catch((e) =>
        notifyError(e.message, 4000)
      );
      transcriberRef.current = new AAITranscriber(token);
    } else if (transcriptModel === "deepgram") {
      const token = await getDeepgramToken().catch((e) =>
        notifyError(e.message, 4000)
      );
      transcriberRef.current = new DeepgramTranscriber(token);
    }
  };

  const startRecording = async () => {
    if (!authToken) {
      notifyError("Login is required to use this feature.", 4000);
      navigate("/login");
      return;
    }

    setState("halting");
    setTranscript("");

    const texts = {};
    await getTranscriber();

    if (!transcriberRef.current) {
      setState("idle");
      return;
    }

    transcriberRef.current.listen.ontranscript = async (message) => {
      setTranscript("");
      if (boxRef.current) {
        boxRef.current.scrollTop = boxRef.current.scrollHeight;
      }
      texts[message.start] = message.text;
      const keys = Object.keys(texts);
      keys.sort((a, b) => a - b);

      for (const key of keys) {
        if (texts[key]) {
          setTranscript((prev) => (prev += `${texts[key]}\n`));
        }
      }
    };

    transcriberRef.current.listen.onfinal = checkQuestion;

    await transcriberRef.current.getMicrophone().catch((e) => {
      if (e.name === "NotAllowedError") {
        notifyError("Microphone access is required to use this feature.", 4000);
      } else if (e.name === "NotFoundError") {
        notifyError("No microphone detected.", 4000);
      }
      setState("idle");
    });

    await transcriberRef.current
      .connect()
      .then(() => setState("recording"))
      .catch((e) => {
        notifyError(e.message, 4000);
        setState("idle");
      });
  };

  useEffect(() => {
    if (transcriberRef?.current?.listen) {
      if (isAnswering) {
        transcriberRef.current.listen.onfinal = doNothing;
      } else {
        transcriberRef.current.listen.onfinal = checkQuestion;
      }
    }
  }, [isAnswering]);

  // This useEffect updates the timer
  useEffect(() => {
    if (state === "empty") {
      return;
    }

    if (state === "idle") {
      setTimer(0);
      return;
    }

    const updateTimer = () => {
      setTimer(val => val + 1);
    }

    const timerInterval = setInterval(updateTimer, 1000);

    return () => clearInterval(timerInterval);
  }, [state])

  // Returns formatted time of the recording
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const secs = (seconds % 60).toString().padStart(2, '0');
    return `${hours}:${minutes}:${secs}`;
  };

  const handleAskManualQuestion = (question) => {
    setChatHistory((prev) => [...prev, { question, assistant }]);
    setState("idle");
  };

  const handleReset = () => {
    setChatHistory([]);
    setSummary("");
    threadIdRef.current = null;
    setState("empty");
  };

  // On load, fetch all brains
  useEffect(() => {
    const fetchAllBrains = async () => {
      let fetchedBrains = [];
      try {
        fetchedBrains = await fetchBrains();
      } catch (e) {
        notifyError(e.message, 4000);
      }
      setAllBrains([{ id: "default", name: "Default"}, ...fetchedBrains]);
    }

    fetchAllBrains();
  }, []);

  // Update chat messages when session messages are fetched
  useEffect(() => {
    if (sessionMessages.length > 0) {
      setChatHistory(sessionMessages);
    }
  }, [sessionMessages]);

  const handleSelectBrain = (event) => {
    const id = event.target.value;
    if (id !== assistant) {
      setAssistant(id);
    }
  };

  return (
    <WhiteHeaderSidebarLayout
      handleReset={(!summary || state !== "idle") ? undefined : handleReset}
      leftDrawer={<AllCases />}
    >
      <Box
        sx={{
          width: isDrawerOpen ? "calc(100% - 500px)" : "100%",
          transition: "width 0.3s ease-in-out",
          overflowX: "hidden",
          maxWidth: "100vw"
        }}
      >
        <Box sx={styles.topRow}>
          <Typography sx={styles.nameLabel}>
            {customer?.first_name || ""}
          </Typography>

          <Box sx={styles.timer}>
            <span style={styles.dot}></span>
            <Typography sx={styles.timerText}>
              {formatTime(timer)}
            </Typography>
          </Box>

          <IconButton
            sx={{
              ...styles.toggleButton,
              transform: isDrawerOpen ? "rotate(0deg)" : "rotate(180deg)"
            }}
            onClick={handleDrawerToggle}
          >
            <DoubleArrow />
          </IconButton>
        </Box>

        <Box>
          <Box>
            <ChatBox
              chatHistory={chatHistory}
              setChatHistory={setChatHistory}
              processModel={processModel}
              threadIdRef={threadIdRef}
              setIsAnswering={setIsAnswering}
              customer={customer}
              state={state}
              startRecording={startRecording}
              stopRecording={stopRecording}
              handleAskManualQuestion={handleAskManualQuestion}
            />
          </Box>
          <Drawer
            open={isDrawerOpen}
            customer={customer}
            setCustomer={setCustomer}
            state={state}
            transcript={transcript}
            setTranscript={setTranscript}
            boxRef={boxRef}
            allBrains={allBrains}
            onSelectedBrainChange={handleSelectBrain}
            selectedBrain={assistant}
          />
        </Box>
      </Box>

    </WhiteHeaderSidebarLayout>
  );
}
